import React from "react";
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  Divider,
  Code,
  List,
  ListItem,
} from "@chakra-ui/react";

const sections = [
  {
    heading: "Java Fundamentals Mastery",
    paragraphs: [],
  },
  {
    heading: "SECTION 1 — ARRAYS (1D, char[], 2D)",
    paragraphs: [],
    topics: [
      {
        title: "1.1 Arrays — Basic Declaration & Looping",
        concepts: [
          "Arrays store fixed-size sequential data.",
          "Arrays use .length (not length()).",
          "Index-based loop is the standard pattern.",
          "Enhanced for-loop is used for iteration when index is not required.",
        ],
        code: `int[] arr = {3, 7, 11, 2, 9};
for (int i = 0; i < arr.length; i++) {
    System.out.println(arr[i]);
}`,
        extraHeading: "Sum pattern",
        extraCode: `int sum = 0;
for (int i = 0; i < arr.length; i++) {
    sum += arr[i];
}
System.out.println("Sum = " + sum);`,
      },
      {
        title: "1.2 Finding Max in Array",
        concepts: [
          "Initialize max with arr[0], not 0.",
          "Iterate through array and update.",
        ],
        code: `int max = arr[0];
for (int i = 1; i < arr.length; i++) {
    if (arr[i] > max) {
        max = arr[i];
    }
}
System.out.println("Max = " + max);`,
      },
      {
        title: "1.3 Arrays of multiple types (int[], double[], String[])",
        code: `int[] ages = {10, 20, 30};
double[] scores = {95.5, 88.0, 76.25};
String[] names = {"Alice", "Bob", "Charlie"};

for (int age : ages) System.out.println(age);
for (double s : scores) System.out.println(s);
for (String n : names) System.out.println(n);`,
      },
      {
        title: "1.4 char[] and String.toCharArray()",
        concepts: [
          "Strings can be converted into char arrays.",
          "Perfect for character-by-character processing.",
        ],
        code: `String s = "netflixrocks";
char[] chars = s.toCharArray();
int count = 0;

for (char c : chars) {
    if (c=='a' || c=='e' || c=='i' || c=='o' || c=='u') count++;
}
System.out.println("Total vowels: " + count);`,
      },
      {
        title: "1.5 2D Arrays (Matrix)",
        concepts: [
          "2D arrays are arrays of arrays.",
          "matrix.length → number of rows",
          "matrix[i].length → number of columns in row i",
        ],
        code: `int[][] matrix = {
    {1,2,3},
    {4,5,6},
    {7,8,9}
};`,
        extraHeading: "Printing grid",
        extraCode: `for (int i = 0; i < matrix.length; i++) {
    for (int j = 0; j < matrix[i].length; j++) {
        System.out.print(matrix[i][j] + " ");
    }
    System.out.println();
}`,
        extraHeading2: "Summing matrix",
        extraCode2: `int sum = 0;
for (int i = 0; i < matrix.length; i++) {
    for (int j = 0; j < matrix[i].length; j++) {
        sum += matrix[i][j];
    }
}
System.out.println("Total sum = " + sum);`,
      },
    ],
  },
  {
    heading: "SECTION 2 — ARRAYLIST",
    topics: [
      {
        title: "2.1 Basics: add(), set(), remove(), size(), loops",
        concepts: [
          "ArrayList is dynamic and resizes.",
          "Fast random access; slow insertion in middle.",
        ],
        code: `List<Integer> list = new ArrayList<>(Arrays.asList(10, 20, 30, 40));
list.set(1, 25);
list.remove(0);
System.out.println(list.size());
for (int x : list) System.out.println(x);
for (int i = 0; i < list.size(); i++) System.out.println(list.get(i));`,
      },
      {
        title: "2.2 Searching in ArrayList (Important)",
        concepts: [
          "DO NOT use == for Strings → use equals().",
          "indexOf() finds only first match; interviewers want manual scanning.",
        ],
        code: `boolean found = false;
for (int i = 0; i < names.size(); i++) {
    if ("Bob".equals(names.get(i))) {
        System.out.println("Bob found at index: " + i);
        found = true;
    }
}
if (!found) System.out.println("Bob not found");`,
      },
      {
        title: "2.3 Sorting ArrayLists",
        code: `Collections.sort(nums); // ascending
nums.sort((a, b) -> b - a); // descending
Collections.sort(words); // strings asc
words.sort((a, b) -> b.compareTo(a)); // strings desc`,
      },
    ],
  },
  {
    heading: "SECTION 3 — LINKEDLIST",
    topics: [
      {
        title: "3.1 LinkedList as Queue",
        concepts: [
          "add() or addLast() for enqueue",
          "removeFirst() for dequeue",
        ],
        code: `LinkedList<Integer> queue = new LinkedList<>();
queue.add(10);
queue.add(20);
queue.add(30);

int removed = queue.removeFirst();
System.out.println("Dequeued: " + removed);`,
      },
    ],
  },
  {
    heading: "SECTION 4 — STRING RULES LEARNED",
    topics: [
      {
        title: "Never use == for String comparison",
        code: `"Bob".equals(name); // correct
// name == "Bob" // wrong`,
      },
      {
        title: "toCharArray() usage mastered",
        concepts: ["Convert String into character sequence for scanning."],
      },
    ],
  },
  {
    heading: "SECTION 5 — LOOPS MASTERED",
    topics: [
      {
        title: "Index loop pattern",
        code: `for (int i = 0; i < arr.length; i++) {
    // ...
}`,
      },
      {
        title: "Enhanced for-loop pattern",
        code: `for (int val : arr) {
    // ...
}`,
      },
      {
        title: "Nested loops for matrix",
        code: `for (int i = 0; i < m.length; i++)
    for (int j = 0; j < m[i].length; j++)
        // ...`,
      },
    ],
  },
  {
    heading: "SECTION 6 — COMMON MISTAKES FIXED",
    topics: [
      {
        title: "Correct patterns",
        concepts: [
          "Use arr.length, not arr.length().",
          "Use equals for Strings, not ==.",
          "Print actual indices, not indexOf() when scanning manually.",
          "Avoid extra i++ inside loops.",
          "Use Arrays.toString(arr) to print arrays.",
        ],
        code: `System.out.println(Arrays.toString(arr));`,
      },
    ],
  },
  {
    heading: "SECTION 7 — CONCEPTUAL UNDERSTANDING YOU ACHIEVED",
    concepts: [
      "Array vs ArrayList vs LinkedList differences",
      "When to use enhanced loop vs index loop",
      "Sorting integers and strings",
      "Searching manually by scanning",
      "Queue behavior using LinkedList",
      "toCharArray and char iteration",
      "Matrix traversal patterns",
      "Summation and max finding logic",
    ],
  },
  {
    heading: "SECTION 8 — WHAT IS STILL LEFT (Next Steps)",
    concepts: [
      "ArrayList: remove by value vs index, sorting custom objects, sublist, contains/indexOf/lastIndexOf",
      "LinkedList: stack operations, removing while iterating, basic LRU",
      "HashMap: frequency counting, map of lists, sorting entries, word count, anagrams",
      "PriorityQueue: min/max heap, PQ of objects, top-K problems",
      "Streams: map, filter, sorted, groupingBy",
      "DSA: sliding window, two pointers, stack problems, BFS/DFS, tree traversal, DP basics",
      "Final phase: interview sims, top-K/LRU/logs/rate limiter/worker pool",
    ],
  },
  {
    heading: "FINAL NOTES",
    paragraphs: [
      "This document is your complete revision kit for everything you have learned so far — no steps, patterns, or explanations have been omitted. Every concept is written in interview-friendly format.",
    ],
  },
];

const SectionBlock = ({ section }) => (
  <Box p={6} shadow="md" borderWidth="1px" borderRadius="md" bg="white">
    <Heading as="h2" size="lg" mb={3}>
      {section.heading}
    </Heading>
    {section.paragraphs?.map((p, idx) => (
      <Text key={idx} mb={3}>
        {p}
      </Text>
    ))}
    {section.concepts && (
      <List spacing={2} pl={4} styleType="disc" mb={4}>
        {section.concepts.map((c, idx) => (
          <ListItem key={idx}>{c}</ListItem>
        ))}
      </List>
    )}
    {section.topics &&
      section.topics.map((topic, idx) => (
        <Box key={idx} mb={6}>
          <Heading as="h3" size="md" mb={2}>
            {topic.title}
          </Heading>
          {topic.concepts && (
            <List spacing={2} pl={4} styleType="disc" mb={3}>
              {topic.concepts.map((c, i) => (
                <ListItem key={i}>{c}</ListItem>
              ))}
            </List>
          )}
          {topic.code && (
            <Box bg="gray.800" p={4} borderRadius="md" mb={3} overflowX="auto">
              <Code colorScheme="teal" whiteSpace="pre-wrap">
                {topic.code}
              </Code>
            </Box>
          )}
          {topic.extraHeading && (
            <Text fontWeight="bold" mb={1}>
              {topic.extraHeading}
            </Text>
          )}
          {topic.extraCode && (
            <Box bg="gray.800" p={4} borderRadius="md" mb={3} overflowX="auto">
              <Code colorScheme="orange" whiteSpace="pre-wrap">
                {topic.extraCode}
              </Code>
            </Box>
          )}
          {topic.extraHeading2 && (
            <Text fontWeight="bold" mb={1}>
              {topic.extraHeading2}
            </Text>
          )}
          {topic.extraCode2 && (
            <Box bg="gray.800" p={4} borderRadius="md" overflowX="auto">
              <Code colorScheme="yellow" whiteSpace="pre-wrap">
                {topic.extraCode2}
              </Code>
            </Box>
          )}
        </Box>
      ))}
  </Box>
);

const LearnJava = () => (
  <Container maxW="container.xl" py={8}>
    <VStack spacing={8} align="stretch">
      <Heading as="h1" size="2xl" textAlign="center">
        Java Fundamentals Mastery
      </Heading>
      <Text fontSize="lg" textAlign="center" color="gray.600">
        Arrays, collections, loops, string rules, and next-step roadmap in one concise revision sheet.
      </Text>
      <Divider />
      {sections.map((section, idx) => (
        <SectionBlock key={idx} section={section} />
      ))}
    </VStack>
  </Container>
);

export default LearnJava;
